@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.screens_driver

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import org.example.project.engineslot.SLOT_BEREICH_WE
import org.example.project.engineslot.loadSlotMeineAuflieger
import org.example.project.engineslot.loadSlotMeineZugmaschinen
import org.example.project.engineslot.slotDisplayDate
import org.example.project.engineslot.slotVehicleDropdownOptions
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.AppDropdownMenuMode
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.utils.All
import org.example.project.utils.LocalizationManager
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.value
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

data class DriverPreparationResult(
    val vorgeladen: Boolean,
    val vorgeladenPaletten: Int,
    val leergutDabei: Boolean,
    val leerpaletten: Int,
    val lkwKennzeichen: String,
    val aufliegerKennzeichen: String
)

@Composable
fun DriverPreparationPopup(
    card: DriverBookingCardModel,
    onSave: (DriverPreparationResult) -> Unit,
    onCancel: () -> Unit
) {
    val t: (String) -> String = { LocalizationManager.get(it) }
    Style(DriverOrderStyles)

    var vorgeladen by remember(card.booking.id) { mutableStateOf<Boolean?>(null) }
    var vorgeladenPaletten by remember(card.booking.id) { mutableStateOf("") }
    var leergutDabei by remember(card.booking.id) { mutableStateOf<Boolean?>(null) }
    var leerpaletten by remember(card.booking.id) { mutableStateOf("") }
    var fahrzeugdatenStimmen by remember(card.booking.id) { mutableStateOf<Boolean?>(null) }
    var lkwKennzeichen by remember(card.booking.id) { mutableStateOf(card.booking.lkwkennzeichen) }
    var aufliegerKennzeichen by remember(card.booking.id) { mutableStateOf(card.booking.aufliegerkennzeichen) }
    var zugmaschinen by remember { mutableStateOf<List<All>>(emptyList()) }
    var auflieger by remember { mutableStateOf<List<All>>(emptyList()) }
    val vehicleOnly = card.artCode == SLOT_BEREICH_WE
    val tauschpalettenRelevant = !vehicleOnly && card.palettentauschAktiv
    val criteriaComplete = vehicleOnly ||
        vorgeladen != null &&
        (!tauschpalettenRelevant || leergutDabei != null) &&
        fahrzeugdatenStimmen != null

    fun savePreparation() {
        onSave(
            DriverPreparationResult(
                vorgeladen = vorgeladen == true,
                vorgeladenPaletten = vorgeladenPaletten.toIntOrNull() ?: 0,
                leergutDabei = tauschpalettenRelevant && leergutDabei == true,
                leerpaletten = if (tauschpalettenRelevant) leerpaletten.toIntOrNull() ?: 0 else 0,
                lkwKennzeichen = lkwKennzeichen.trim(),
                aufliegerKennzeichen = aufliegerKennzeichen.trim()
            )
        )
    }

    LaunchedEffect(Unit) {
        zugmaschinen = loadSlotMeineZugmaschinen()
        auflieger = loadSlotMeineAuflieger()
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            width(100.percent)
            minHeight(100.percent)
            padding(18.px, 16.px, 28.px, 16.px)
            boxSizing("border-box")
        }
    }) {
        Div({
            style {
                width(100.percent)
                maxWidth(420.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(14.px)
                color(Color.white)
                property("box-sizing", "border-box")
            }
        }) {
            if (vehicleOnly) {
                Div({
                    classes(DriverOrderStyles.tile)
                    style {
                        minHeight(0.px)
                    }
                }) {
                    P({
                        style {
                            margin(0.px)
                            fontSize(16.px)
                            lineHeight("1.45")
                            color(Color.white)
                            fontWeight("700")
                        }
                    }) { Text(t("driver_confirm_vehicle_data")) }
                    DriverPopupDropdown(
                        label = t("driver_truck"),
                        options = vehicleKennzeichenOptions(zugmaschinen, lkwKennzeichen),
                        selected = lkwKennzeichen,
                        onChange = { lkwKennzeichen = it }
                    )
                    DriverPopupDropdown(
                        label = t("driver_trailer"),
                        options = vehicleKennzeichenOptions(auflieger, aufliegerKennzeichen),
                        selected = aufliegerKennzeichen,
                        onChange = { aufliegerKennzeichen = it }
                    )
                }
            } else {
                DriverPopupWaCriteriaCard(
                    vorgeladen = vorgeladen,
                    onVorgeladen = { vorgeladen = it },
                    vorgeladenPaletten = vorgeladenPaletten,
                    onVorgeladenPaletten = { vorgeladenPaletten = it.filter(Char::isDigit) },
                    tauschpalettenRelevant = tauschpalettenRelevant,
                    leergutDabei = leergutDabei,
                    onLeergutDabei = { leergutDabei = it },
                    leerpaletten = leerpaletten,
                    onLeerpaletten = { leerpaletten = it.filter(Char::isDigit) },
                    fahrzeugdatenStimmen = fahrzeugdatenStimmen,
                    onFahrzeugdatenStimmen = { fahrzeugdatenStimmen = it },
                    lkwKennzeichen = lkwKennzeichen,
                    aufliegerKennzeichen = aufliegerKennzeichen,
                    zugmaschinen = zugmaschinen,
                    auflieger = auflieger,
                    onLkwChange = { lkwKennzeichen = it },
                    onAufliegerChange = { aufliegerKennzeichen = it }
                )
            }

            if (!vehicleOnly) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.FlexEnd)
                        gap(12.px)
                        flexWrap(FlexWrap.Wrap)
                        paddingTop(8.px)
                    }
                }) {
                    LumaticaButton(t("abbrechen"), onCancel, LumaticaButtonVariant.Standard)
                    LumaticaButton(
                        label = t("driver_to_yard"),
                        onClick = { savePreparation() },
                        variant = LumaticaButtonVariant.Action,
                        enabled = criteriaComplete
                    )
                }
            }
        }

        if (vehicleOnly) {
            Div({
                style {
                    width(100.percent)
                    maxWidth(420.px)
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.Center)
                    gap(12.px)
                    flexWrap(FlexWrap.Wrap)
                    padding(10.px, 16.px, 0.px, 16.px)
                    boxSizing("border-box")
                }
            }) {
                LumaticaButton(t("zurück"), onCancel, LumaticaButtonVariant.Standard)
                LumaticaButton(
                    label = t("driver_to_yard"),
                    onClick = {
                        onSave(
                            DriverPreparationResult(
                                vorgeladen = false,
                                vorgeladenPaletten = 0,
                                leergutDabei = false,
                                leerpaletten = 0,
                                lkwKennzeichen = lkwKennzeichen.trim(),
                                aufliegerKennzeichen = aufliegerKennzeichen.trim()
                            )
                        )
                    },
                    variant = LumaticaButtonVariant.Action
                )
            }
        }
    }
}

@Composable
private fun DriverPopupWaCriteriaCard(
    vorgeladen: Boolean?,
    onVorgeladen: (Boolean) -> Unit,
    vorgeladenPaletten: String,
    onVorgeladenPaletten: (String) -> Unit,
    tauschpalettenRelevant: Boolean,
    leergutDabei: Boolean?,
    onLeergutDabei: (Boolean) -> Unit,
    leerpaletten: String,
    onLeerpaletten: (String) -> Unit,
    fahrzeugdatenStimmen: Boolean?,
    onFahrzeugdatenStimmen: (Boolean) -> Unit,
    lkwKennzeichen: String,
    aufliegerKennzeichen: String,
    zugmaschinen: List<All>,
    auflieger: List<All>,
    onLkwChange: (String) -> Unit,
    onAufliegerChange: (String) -> Unit
) {
    Div({
        classes(DriverOrderStyles.tile)
        style {
            minHeight(0.px)
        }
    }) {
        DriverPopupQuestionBlock(LocalizationManager.get("driver_preloaded_question"), vorgeladen, onVorgeladen)
        if (vorgeladen == true) {
            DriverPopupNumberInput(
                label = LocalizationManager.get("driver_preloaded_pallet_count"),
                value = vorgeladenPaletten,
                onChange = onVorgeladenPaletten
            )
        }

        if (tauschpalettenRelevant) {
            DriverPopupQuestionBlock(LocalizationManager.get("driver_empty_exchange_pallets_question"), leergutDabei, onLeergutDabei)
            if (leergutDabei == true) {
                DriverPopupNumberInput(
                    label = LocalizationManager.get("driver_empty_exchange_pallet_count"),
                    value = leerpaletten,
                    onChange = onLeerpaletten
                )
            }
        } else {
            P({
                style {
                    margin(0.px)
                    padding(10.px, 12.px)
                    borderRadius(8.px)
                    backgroundColor(rgba(255, 176, 32, 0.14))
                    border(1.px, LineStyle.Solid, rgba(255, 176, 32, 0.34))
                    color(Color("#ffd28a"))
                    fontSize(13.px)
                    fontWeight("800")
                    lineHeight("1.35")
                }
            }) {
                Text(LocalizationManager.get("driver_no_pallet_exchange_warning"))
            }
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(8.px)
            }
        }) {
            DriverPopupQuestion(LocalizationManager.get("driver_vehicle_data_correct_question"))
            DriverPopupVehicleLine(LocalizationManager.get("driver_truck"), lkwKennzeichen.ifBlank { "-" })
            DriverPopupVehicleLine(LocalizationManager.get("driver_trailer"), aufliegerKennzeichen.ifBlank { "-" })
            DriverPopupYesNo(fahrzeugdatenStimmen, onFahrzeugdatenStimmen)
        }
        if (fahrzeugdatenStimmen == false) {
            DriverPopupDropdown(
                label = LocalizationManager.get("driver_truck"),
                options = slotVehicleDropdownOptions(zugmaschinen, lkwKennzeichen),
                selected = lkwKennzeichen,
                onChange = onLkwChange
            )
            DriverPopupDropdown(
                label = LocalizationManager.get("driver_trailer"),
                options = slotVehicleDropdownOptions(auflieger, aufliegerKennzeichen),
                selected = aufliegerKennzeichen,
                onChange = onAufliegerChange
            )
        }
    }
}

@Composable
private fun DriverPopupQuestionBlock(text: String, value: Boolean?, onChange: (Boolean) -> Unit) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(8.px)
        }
    }) {
        DriverPopupQuestion(text)
        DriverPopupYesNo(value, onChange)
    }
}

@Composable
private fun DriverPopupOrderBlock(card: DriverBookingCardModel) {
    Div({
        style {
            padding(14.px, 16.px)
            borderRadius(18.px)
            property("background", "rgba(255,255,255,0.05)")
            border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.12))
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(6.px)
        }
    }) {
        Span({
            style {
                color(rgba(255, 255, 255, 0.55))
                fontSize(11.px)
                fontWeight("700")
                property("letter-spacing", "0.08em")
                property("text-transform", "uppercase")
            }
        }) { Text(LocalizationManager.get("driver_order")) }
        Span({
            style {
                color(Color.white)
                fontSize(18.px)
                fontWeight("700")
            }
        }) { Text(card.primaryRef.ifBlank { LocalizationManager.get("driver_without_reference") }) }
        Span({
            style {
                color(rgba(255, 255, 255, 0.72))
                fontSize(14.px)
            }
        }) {
            val date = card.booking.datum.ifBlank { card.booking.verladedatum }
            val time = card.booking.uhrzeitVon.ifBlank { card.booking.verladeuhrzeit }.ifBlank { card.booking.uhrzeitAnmeldung }
            Text("${slotDisplayDate(date)} | ${time.ifBlank { "-" }} ${LocalizationManager.get("driver_time_suffix")}")
        }
        Span({
            style {
                color(rgba(255, 255, 255, 0.72))
                fontSize(14.px)
            }
        }) { Text("${LocalizationManager.get("driver_assigned_by")}: ${card.gebuchtVonLabel.ifBlank { "-" }}") }
    }
}

@Composable
private fun DriverPopupQuestion(text: String) {
    P({
        style {
            margin(0.px)
            fontSize(16.px)
            lineHeight("1.45")
            color(Color.white)
            fontWeight("700")
        }
    }) { Text(text) }
}

@Composable
private fun DriverPopupYesNo(value: Boolean?, onChange: (Boolean) -> Unit) {
    Div({
        style {
            display(DisplayStyle.Flex)
            gap(10.px)
            flexWrap(FlexWrap.Wrap)
        }
    }) {
        DriverPopupChoice(LocalizationManager.get("driver_yes"), value == true) { onChange(true) }
        DriverPopupChoice(LocalizationManager.get("driver_no"), value == false) { onChange(false) }
    }
}

@Composable
private fun DriverPopupChoice(label: String, active: Boolean, onClick: () -> Unit) {
    LumaticaButton(
        label = label,
        onClick = onClick,
        variant = LumaticaButtonVariant.State,
        isActive = active,
        heightPx = 40,
        paddingH = 24
    )
}

@Composable
private fun DriverPopupNumberInput(label: String, value: String, onChange: (String) -> Unit) {
    Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(6.px) } }) {
        Span({ style { color(rgba(255, 255, 255, 0.62)); fontSize(12.px); fontWeight("700") } }) { Text(label) }
        Input(InputType.Text, attrs = {
            value(value)
            style {
                height(42.px)
                padding(0.px, 12.px)
                borderRadius(12.px)
                color(Color.white)
                backgroundColor(Color("rgba(255,255,255,0.06)"))
                border(1.px, LineStyle.Solid, Color("rgba(255,255,255,0.16)"))
            }
            onInput { onChange(it.value) }
        })
    }
}

@Composable
private fun DriverPopupVehicleLine(label: String, value: String) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            gap(12.px)
            color(Color.white)
        }
    }) {
        Span({ style { color(rgba(255, 255, 255, 0.58)); fontSize(12.px); fontWeight("700") } }) { Text(label) }
        Span({ style { fontSize(14.px); textAlign("right") } }) { Text(value) }
    }
}

@Composable
private fun DriverPopupDropdown(label: String, options: List<All>, selected: String, onChange: (String) -> Unit) {
    Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(6.px) } }) {
        Span({ style { color(rgba(255, 255, 255, 0.62)); fontSize(12.px); fontWeight("700") } }) { Text(label) }
        AppDropdown(
            options = options,
            selectedValue = selected.ifBlank { null },
            onChange = onChange,
            placeholder = LocalizationManager.get("wähle_option"),
            appearance = AppDropdownAppearance.Underline,
            menuMode = AppDropdownMenuMode.Overlay
        )
    }
}

private fun vehicleKennzeichenOptions(vehicles: List<All>, selectedKennzeichen: String): List<All> {
    val options = vehicles
        .filter { it.aktiv && it.kennzeichen.isNotBlank() }
        .distinctBy { it.kennzeichen.trim().lowercase() }
        .sortedBy { it.kennzeichen.lowercase() }
        .map { vehicle ->
            val kennzeichen = vehicle.kennzeichen.trim()
            All(key = kennzeichen, label = kennzeichen)
        }
    val selected = selectedKennzeichen.trim()
    val selectedOption = if (selected.isNotBlank() && options.none { it.key.equals(selected, ignoreCase = true) }) {
        listOf(All(key = selected, label = selected))
    } else {
        emptyList()
    }
    return listOf(All(key = "", label = LocalizationManager.get("driver_no_selection"))) + selectedOption + options
}
